import { format } from "date-fns";
import {
  Quote,
  OccupantSelectionItem,
  AddMissingProductModalState,
} from "../../../types/IMultiLocationQuoteMobile";
import IRouteValidationResult from "../../competitiveAnalysisModal/components/RouteValidation/types/IRouteValidation";
import { ISubmitDataRequestFormParams } from "../../../types/IRailRateRequest";
import type {
  MissingRailFreightSectionItem,
  MultiRouteValidationOdPair,
  MultiRouteValidationParams,
  PendingTruckValidation,
} from "../../../types/IMissingRailFreight";
import ILocation, { IOccupant } from "../../../types/ILocation";
import NitroService from "../../../utils/nitro.service";
import {
  normalizeOriginManagerPrice,
  OriginManagerPrice,
} from "../../../utils/utils";
import { IFavoriteSetExtension } from "../../../types/IFavoriteOrigins";

const getOriginRowKey = (originId?: string, competitor?: boolean): string => {
  if (!originId) return "";
  const rowType = competitor === true ? "competitor" : "koch";
  return `${rowType}-${originId}`;
};

export const getQuoteIdentityKey = (quoteEntry: Quote): string => {
  const rowType = quoteEntry.competitor === true ? "competitor" : "koch";
  const originId = quoteEntry.origin?.id || "";
  const mode = (quoteEntry.mode || "").toUpperCase();
  return `${rowType}-${originId}-${mode}`;
};

export const buildMarketerName = (
  givenName?: string,
  familyName?: string,
): string => {
  return `${givenName ?? ""} ${familyName ?? ""}`.trim();
};

export type KochDlvdMode = {
  truck: "px" | "alt";
  rail: "px" | "alt";
};

export const normalizeDlvdOption = (value: unknown): "px" | "alt" | null => {
  if (value === "px" || value === "alt") {
    return value;
  }

  return null;
};

export const resolveKochDlvdModeFromDestination = (
  destinationEntry: any,
): KochDlvdMode => {
  const truckDlvdOption =
    normalizeDlvdOption(destinationEntry?.truckDlvdOption) ??
    normalizeDlvdOption(destinationEntry?.destination?.truckDlvdOption) ??
    "px";

  const railDlvdOption =
    normalizeDlvdOption(destinationEntry?.railDlvdOption) ??
    normalizeDlvdOption(destinationEntry?.destination?.railDlvdOption) ??
    "px";

  return {
    truck: truckDlvdOption,
    rail: railDlvdOption,
  };
};

export const saveKochDlvdOptions = async (
  nextMode: KochDlvdMode,
  givenName?: string,
  familyName?: string,
): Promise<void> => {
  await NitroService.saveDlvdOptions({
    marketer: buildMarketerName(givenName, familyName),
    truckDlvdOption: nextMode.truck,
    railDlvdOption: nextMode.rail,
  });
};

export const buildDestinationsOriginsRequest = ({
  currentFilters,
  destinationName,
  selectedProduct,
  fallbackProduct,
  selectedMonth,
  currency,
  uom,
  marketer,
}: {
  currentFilters: any;
  destinationName?: string;
  selectedProduct?: string;
  fallbackProduct?: string;
  selectedMonth?: string;
  currency: string;
  uom: string;
  marketer: string;
}): {
  id: string;
  product: string;
  party: string | null;
  marketer: string;
  shipDate: string;
  currency: string;
  uom: string;
} => {
  const party = currentFilters?.party || null;
  const destination = party
    ? ""
    : currentFilters?.city ||
      currentFilters?.state ||
      currentFilters?.location ||
      destinationName ||
      "";

  return {
    id: destination,
    product: selectedProduct || fallbackProduct || "AMMONIA",
    party,
    marketer,
    shipDate: selectedMonth || "",
    currency,
    uom,
  };
};

export const getFavoriteOriginType = (quote: Quote): "koch" | "competitor" =>
  quote.competitor ? "competitor" : "koch";

export const occupantMatchesFavoriteType = (
  occupant: IOccupant,
  quote: Quote,
): boolean => {
  const targetType = getFavoriteOriginType(quote);
  const occupantTypes = occupant.types
    .map((type) => type.toLowerCase())
    .filter(Boolean);

  return occupantTypes.includes(targetType);
};

export const generateMonths = (): Array<{ display: string; value: string }> => {
  return Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() + i);
    return {
      display: format(date, "MMM yy''"),
      value: format(date, "yyyy-MM-dd"),
    };
  });
};

export const generateSplitAwareMonths = (
  splitMonthKeys: string[],
): Array<{ display: string; value: string }> => {
  const splitSet = new Set(splitMonthKeys);
  const result: Array<{ display: string; value: string }> = [];
  for (let i = 0; i < 12; i += 1) {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + i);
    const monthKey = format(date, "yyyy-MM");
    if (splitSet.has(monthKey)) {
      const abbr = format(date, "MMM");
      result.push({ display: `${abbr} - 1H`, value: `${monthKey}-01` });
      result.push({ display: `${abbr} - 2H`, value: `${monthKey}-16` });
    } else {
      result.push({
        display: format(date, "MMMM"),
        value: format(date, "yyyy-MM-dd"),
      });
    }
  }
  return result;
};

export const normalizeMonthSelectionValue = (value?: string): string => {
  if (!value) return "";
  const raw = String(value);
  const match = raw.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!match) {
    return raw.slice(0, 10);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return raw.slice(0, 10);
  }

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0",
  )}`;
};

export const findSelectedMonthOption = (
  months: Array<{ display: string; value: string }>,
  selectedMonth?: string,
): { display: string; value: string } | undefined => {
  const toDisplayMonthAbbr = (option?: { display: string; value: string }) => {
    if (!option) return undefined;
    if (option.display.includes(" - ")) return option;

    const normalizedValue = normalizeMonthSelectionValue(option.value);
    const parsedDate = new Date(`${normalizedValue}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return option;

    return {
      ...option,
      display: format(parsedDate, "MMM"),
    };
  };

  const normalizedSelected = normalizeMonthSelectionValue(selectedMonth);
  if (!normalizedSelected) return undefined;

  const exact = months.find(
    (option) =>
      normalizeMonthSelectionValue(option.value) === normalizedSelected,
  );
  if (exact) return toDisplayMonthAbbr(exact);

  const monthKey = normalizedSelected.slice(0, 7);
  const day = Number(normalizedSelected.slice(8, 10));
  const halfTarget = Number.isFinite(day) && day >= 16 ? "16" : "01";

  const halfMatch = months.find(
    (option) => option.value === `${monthKey}-${halfTarget}`,
  );
  if (halfMatch) return toDisplayMonthAbbr(halfMatch);

  return toDisplayMonthAbbr(
    months.find((option) => option.value.startsWith(monthKey)),
  );
};

export const filterTruckQuotes = (
  quotes: Quote[],
  enabledOriginIds: Set<string>,
): Quote[] => {
  const truckQuotes = quotes.filter((q) => q.mode?.toUpperCase() === "TRUCK");
  return truckQuotes.filter(
    (q) =>
      q.origin.id &&
      enabledOriginIds.has(getOriginRowKey(q.origin.id, q.competitor)),
  );
};

export const filterRailQuotes = (
  quotes: Quote[],
  enabledOriginIds: Set<string>,
): Quote[] => {
  const railQuotes = quotes.filter((q) => q.mode?.toUpperCase() !== "TRUCK");
  return railQuotes.filter(
    (q) =>
      q.origin.id &&
      enabledOriginIds.has(getOriginRowKey(q.origin.id, q.competitor)),
  );
};

export const filterQuotesByOutboundMode = (
  quotes: Quote[],
  mode: "TRUCK" | "RAIL",
): Quote[] =>
  quotes.filter((quote) => {
    const outboundModes = quote.origin?.details?.outboundModes;
    return (
      Array.isArray(outboundModes) &&
      outboundModes.some(
        (outboundMode: unknown) => String(outboundMode).toUpperCase() === mode,
      )
    );
  });

type DlvdOption = "px" | "alt";

const resolveOriginManagerPrice = (
  quote: Quote,
  options?: {
    truckDlvdOption?: DlvdOption;
    railDlvdOption?: DlvdOption;
  },
): OriginManagerPrice => {
  const mode = (quote.mode || "").toUpperCase();
  const isKochOrigin = quote.competitor !== true;
  const truckDlvdOption = options?.truckDlvdOption || "px";
  const railDlvdOption = options?.railDlvdOption || "px";

  let selectedPrice: unknown = quote.maxPrice;

  if (isKochOrigin && mode === "TRUCK") {
    selectedPrice =
      truckDlvdOption === "px" ? quote.postedPrice : quote.truckPrice;
  } else if (isKochOrigin && mode === "RAIL") {
    selectedPrice =
      railDlvdOption === "px" ? quote.postedPrice : quote.railPrice;
  } else if (!isKochOrigin && mode === "TRUCK") {
    selectedPrice = quote.truckPrice ?? quote.postedPrice;
  } else if (!isKochOrigin && mode === "RAIL") {
    selectedPrice = quote.railPrice ?? quote.postedPrice;
  }

  const normalized = normalizeOriginManagerPrice(selectedPrice);
  if (normalized != null) {
    return normalized;
  }

  return normalizeOriginManagerPrice(quote.maxPrice);
};

export const calculateDlvd = (
  quote: Quote,
  quoteKey: string,
  nbOverrides: Map<string, number>,
  dlvdOfferInput: string,
  options?: {
    truckDlvdOption?: DlvdOption;
    railDlvdOption?: DlvdOption;
  },
): string => {
  if (dlvdOfferInput) {
    const inputValue = parseFloat(dlvdOfferInput);
    if (!Number.isNaN(inputValue)) {
      return `$${inputValue.toFixed(0)}`;
    }
  }

  const originManagerPrice = resolveOriginManagerPrice(quote, {
    truckDlvdOption: options?.truckDlvdOption,
    railDlvdOption: options?.railDlvdOption,
  });

  const shouldUseZeroOverride =
    originManagerPrice === "DISC" || originManagerPrice === "N/O";
  const nbOverride = shouldUseZeroOverride ? 0 : nbOverrides.get(quoteKey);

  if (nbOverride != null && quote.freight?.totalFreight != null) {
    return `$${(nbOverride + quote.freight.totalFreight).toFixed(0)}`;
  }

  const basePriceForDlvd =
    typeof originManagerPrice === "number"
      ? originManagerPrice
      : quote.maxPrice;

  if (basePriceForDlvd != null && quote.freight?.totalFreight != null) {
    return `$${(basePriceForDlvd + quote.freight.totalFreight).toFixed(0)}`;
  }
  return "$-";
};

export const calculateNb = (
  quote: Quote,
  quoteKey: string,
  nbOverrides: Map<string, number>,
  dlvdOfferInput: string,
  options?: {
    truckDlvdOption?: "px" | "alt";
    railDlvdOption?: "px" | "alt";
  },
): string => {
  const nbOverride = nbOverrides.get(quoteKey);
  if (nbOverride != null) {
    return `$${nbOverride.toFixed(0)}`;
  }

  if (dlvdOfferInput && quote.freight?.totalFreight != null) {
    const inputValue = parseFloat(dlvdOfferInput);
    if (!Number.isNaN(inputValue)) {
      return `$${(inputValue - quote.freight.totalFreight).toFixed(0)}`;
    }
  }

  const originManagerPrice = resolveOriginManagerPrice(quote, {
    truckDlvdOption: options?.truckDlvdOption,
    railDlvdOption: options?.railDlvdOption,
  });

  if (originManagerPrice != null) {
    if (typeof originManagerPrice === "string") {
      return originManagerPrice;
    }
    return `$${originManagerPrice}`;
  }

  if (quote.maxPrice != null) {
    return `$${quote.maxPrice}`;
  }

  return "$-";
};

export const formatProductName = (name: string): string => {
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

export const formatOriginNameDisplay = (originName?: string): string => {
  if (!originName) {
    return "";
  }

  return originName.replace(/[A-Za-z]+/g, (word) => {
    if (/^[A-Z]{2,3}$/.test(word)) {
      return word;
    }

    return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
  });
};

export const filterQuotesWithFreight = (quotes: Quote[]): Quote[] => {
  return quotes.filter((q) => {
    const isKochExternalRailQuote =
      q.mode?.toUpperCase() === "RAIL" &&
      q.competitor !== true &&
      (q.freight as any)?.isExternal === true;

    return !!q.freight && !isKochExternalRailQuote;
  });
};

export const filterQuotesWithoutFreight = (quotes: Quote[]): Quote[] => {
  return quotes.filter((q) => {
    const isKochExternalRailQuote =
      q.mode?.toUpperCase() === "RAIL" &&
      q.competitor !== true &&
      (q.freight as any)?.isExternal === true;

    return !q.freight || isKochExternalRailQuote;
  });
};

export const getEnabledOriginIds = (
  favoriteOriginsByUser: IFavoriteSetExtension[],
  product: string,
): { truckEnabled: Set<string>; railEnabled: Set<string> } => {
  const prod = (product || "AMMONIA").toUpperCase();

  const originsByProduct = favoriteOriginsByUser.find(
    (o) => (o.product || "").toUpperCase() === prod,
  );

  const favorites =
    originsByProduct && Array.isArray(originsByProduct.favoriteLocations)
      ? originsByProduct.favoriteLocations
      : [];

  return {
    truckEnabled: new Set(
      favorites
        .map((fav) => {
          if (!fav) return null;
          const key = getOriginRowKey(
            fav.location?.id,
            fav.competitor === true,
          );
          const enabled = fav.location.outboundModes.includes("TRUCK");
          return enabled ? key : null;
        })
        .filter((k) => k) as string[],
    ),
    railEnabled: new Set(
      favorites
        .map((fav) => {
          if (!fav) return null;
          const key = getOriginRowKey(
            fav.location?.id,
            fav.competitor === true,
          );
          const enabled = fav.location.outboundModes.includes("RAIL");
          return enabled ? key : null;
        })
        .filter((k) => k) as string[],
    ),
  };
};

export const buildCarriersMap = (
  carrierRecords: Array<{ id: string; manuallyCreated: boolean }>,
  freshLocationDetails: ILocation,
  newStation: string,
): Record<string, boolean> => {
  const carriersMap: Record<string, boolean> = {};
  const selectedCarrierIds = freshLocationDetails.carriers.map((c) => c.id);

  carrierRecords.forEach((r) => {
    const isSameStation = freshLocationDetails?.stationState === newStation;
    const isCarrierSelected =
      isSameStation && selectedCarrierIds.includes(r.id);
    carriersMap[r.id] = isSameStation ? isCarrierSelected || false : true;
  });
  return carriersMap;
};

export const getActiveCarriers = (
  railCarriers: Record<string, boolean>,
): string[] => {
  return Object.entries(railCarriers)
    .filter(([, isActive]) => isActive)
    .map(([name]) => name);
};

export const getMissingProductName = (
  quote: Quote,
  fallbackProduct: string,
): string => {
  const { validation } = quote;
  if (!validation) {
    return formatProductName(fallbackProduct);
  }

  const candidates: unknown[] = [
    validation.missingProduct,
    validation.product,
    validation.productName,
    validation.requiredProduct,
    validation.missingProducts,
    validation.productsMissing,
    validation.requiredProducts,
  ];

  const foundProduct = candidates.find((candidate) => {
    if (typeof candidate === "string" && candidate.trim()) {
      return true;
    }

    if (Array.isArray(candidate) && candidate.length > 0) {
      const firstValue = candidate[0];
      return typeof firstValue === "string" && !!firstValue.trim();
    }

    return false;
  });

  if (typeof foundProduct === "string") {
    return formatProductName(foundProduct.trim());
  }

  if (Array.isArray(foundProduct) && foundProduct.length > 0) {
    const firstValue = foundProduct[0];
    if (typeof firstValue === "string") {
      return formatProductName(firstValue.trim());
    }
  }

  return formatProductName(fallbackProduct);
};

export const getOccupantSelectionKey = (
  occupant: any,
  index: number,
): string => {
  return occupant?.id || `${occupant?.name || "occupant"}-${index}`;
};

export type MissingMilesSectionItem = {
  originId: string;
  originName: string;
  competitor: boolean;
  validation: any;
  quote: Quote;
};

export type NoGuidanceSectionItem = {
  originKey: string;
  originId: string;
  originName: string;
  competitor: boolean;
  description: string;
  validation: any;
  quote: Quote;
};

export type MissingFreightSectionsResult = {
  missingProductQuotes: Quote[];
  missingMilesOrigins: MissingMilesSectionItem[];
  noGuidanceOrigins: NoGuidanceSectionItem[];
};

export const getOriginsMissingProductQuotes = ({
  missingFreightTab,
  shouldShowTruckMissing,
  shouldShowRailMissing,
  missingProductQuotes,
}: {
  missingFreightTab: string;
  shouldShowTruckMissing: boolean;
  shouldShowRailMissing: boolean;
  missingProductQuotes: Quote[];
}): Quote[] => {
  const isTruckMissingTabActive =
    shouldShowTruckMissing && missingFreightTab === "TRUCK";
  const isRailMissingTabActive =
    shouldShowRailMissing && missingFreightTab === "RAIL";

  if (!isTruckMissingTabActive && !isRailMissingTabActive) {
    return [];
  }

  return missingProductQuotes;
};

const getTargetOccupantType = (quote: Quote): "koch" | "competitor" =>
  quote.competitor ? "competitor" : "koch";

export const findTypeMatchedOccupant = (
  occupants: IOccupant[],
  quote: Quote,
): any => {
  const targetType = getTargetOccupantType(quote);
  return (
    occupants.find((occupant) => {
      const types = occupant.types
        .map((type) => type.toLowerCase())
        .filter(Boolean);
      return types.includes(targetType);
    }) || null
  );
};

const hasSelectedProductInOccupant = (
  occupant: any,
  selectedProduct: string,
): boolean => {
  const normalizedSelectedProduct = (selectedProduct || "").toUpperCase();
  const products = Array.isArray(occupant?.products) ? occupant.products : [];
  return products.some(
    (entry: any) =>
      typeof entry?.product === "string" &&
      entry.product.toUpperCase() === normalizedSelectedProduct,
  );
};

export const buildNoGuidanceDescription = (validation: any): string => {
  const hasTruckMileage = validation?.hasTruckMileage === true;
  const hasTruckRoute = validation?.hasTruckRoute === true;
  const hasValidTruckRateForShipWindow =
    validation?.hasValidTruckRateForShipWindow === true;
  const hasValidTruckRateForShipWindowAndMileage =
    validation?.hasValidTruckRateForShipWindowAndMileage === true;

  if (!hasTruckRoute) {
    return "No Route";
  }

  if (
    hasTruckMileage &&
    hasTruckRoute &&
    !hasValidTruckRateForShipWindow &&
    !hasValidTruckRateForShipWindowAndMileage
  ) {
    return "Expired";
  }

  if (
    hasTruckMileage &&
    hasTruckRoute &&
    hasValidTruckRateForShipWindow &&
    !hasValidTruckRateForShipWindowAndMileage
  ) {
    const truckMileage = validation?.truckMileage;
    if (typeof truckMileage === "number") {
      return `Outside Mileage Band - ${truckMileage} Mi`;
    }
    return "Outside Mileage Band";
  }

  return "No Guidance";
};

export type TruckValidationSection = "missing-miles" | "no-guidance" | "valid";

export const classifyTruckValidation = (
  validation: any,
): { section: TruckValidationSection; description?: string } => {
  if (validation?.hasTruckRoute === false) {
    return { section: "no-guidance", description: "No Route" };
  }

  if (validation?.hasTruckMileage === false) {
    return { section: "missing-miles" };
  }

  if (validation?.hasValidTruckRateForShipWindowAndMileage !== true) {
    return {
      section: "no-guidance",
      description: buildNoGuidanceDescription(validation),
    };
  }

  return { section: "valid" };
};

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const mapValidationResultsByOriginId = (
  results: IRouteValidationResult[],
): Map<string, IRouteValidationResult> => {
  const map = new Map<string, IRouteValidationResult>();
  results.forEach((result) => {
    const originLocationId = (result as any)?.odPair?.originLocationId;
    if (originLocationId) {
      map.set(originLocationId, result);
    }
  });
  return map;
};

// TODO: error handling

export const buildMissingFreightSections = async ({
  truckMissingQuotesForDisplay,
  destinationId,
  selectedProduct,
  selectedMonth,
  getMultiLocationDetails,
  validateMultiRoute,
}: {
  truckMissingQuotesForDisplay: Quote[];
  destinationId: string;
  selectedProduct: string;
  selectedMonth: string;
  getMultiLocationDetails?: (originIds: string[]) => Promise<Map<string, any>>;
  validateMultiRoute: (
    params: MultiRouteValidationParams,
  ) => Promise<IRouteValidationResult[]>;
}): Promise<MissingFreightSectionsResult> => {
  const byKey = new Map<string, Quote>();
  truckMissingQuotesForDisplay.forEach((quote) => {
    const originId = quote?.origin?.id || "";
    if (!originId) return;
    const key = `${quote.competitor ? "competitor" : "koch"}-${originId}`;
    if (!byKey.has(key)) {
      byKey.set(key, quote);
    }
  });

  const missingProductQuotes: Quote[] = [];
  const missingMilesOrigins: MissingMilesSectionItem[] = [];
  const noGuidanceOrigins: NoGuidanceSectionItem[] = [];

  const uniqueOriginIds = Array.from(byKey.values())
    .map((quote) => quote.origin.id)
    .filter((id): id is string => Boolean(id));

  let prefetchedLocationDetails: Map<string, any> | null | undefined;
  if (getMultiLocationDetails && uniqueOriginIds.length > 0) {
    try {
      prefetchedLocationDetails = await getMultiLocationDetails(
        uniqueOriginIds,
      );
    } catch (error) {
      prefetchedLocationDetails = null;
    }
  }

  const pendingValidations: PendingTruckValidation[] = [];

  const entries = Array.from(byKey.entries());
  entries.forEach(([originKey, quote]) => {
    const originId = quote?.origin?.id || "";
    const originName = quote?.origin?.name || "Unknown Origin";
    const competitor = !!quote?.competitor;

    if (!originId || !destinationId || prefetchedLocationDetails === null) {
      return;
    }

    const locationDetails = prefetchedLocationDetails?.get(originId);
    if (!locationDetails) {
      return;
    }

    const occupants = Array.isArray(locationDetails?.occupants)
      ? locationDetails.occupants
      : [];
    const matchedOccupant = findTypeMatchedOccupant(occupants, quote);

    if (
      !matchedOccupant ||
      !hasSelectedProductInOccupant(matchedOccupant, selectedProduct)
    ) {
      missingProductQuotes.push(quote);
      return;
    }

    const originOccupantId = matchedOccupant?.id || "";
    if (!originOccupantId) {
      noGuidanceOrigins.push({
        originKey,
        originId,
        originName,
        competitor,
        description: "No Guidance",
        validation: {},
        quote,
      });
      return;
    }

    pendingValidations.push({
      originKey,
      originId,
      originName,
      competitor,
      quote,
      odPair: {
        originLocationId: originId,
        originOccupantId,
        destinationId,
        mode: "TRUCK",
      },
    });
  });

  let validationByOriginId = new Map<string, IRouteValidationResult>();

  if (pendingValidations.length > 0) {
    try {
      const odPairs = pendingValidations.map((entry) => entry.odPair);
      const results = await validateMultiRoute({
        odPairsToValidate: odPairs,
        product: selectedProduct,
        shipDate: selectedMonth || "",
      });
      validationByOriginId = mapValidationResultsByOriginId(
        Array.isArray(results) ? results : [],
      );
    } catch (error) {
      // Fall through — all pending entries will land in No Guidance below
    }
  }

  pendingValidations.forEach((entry) => {
    const validation = validationByOriginId.get(entry.originId);

    if (!validation) {
      noGuidanceOrigins.push({
        originKey: entry.originKey,
        originId: entry.originId,
        originName: entry.originName,
        competitor: entry.competitor,
        description: "No Guidance",
        validation: {},
        quote: entry.quote,
      });
      return;
    }

    const classification = classifyTruckValidation(validation);

    if (classification.section === "missing-miles") {
      missingMilesOrigins.push({
        originId: entry.originId,
        originName: entry.originName,
        competitor: entry.competitor,
        validation,
        quote: entry.quote,
      });
      return;
    }

    noGuidanceOrigins.push({
      originKey: entry.originKey,
      originId: entry.originId,
      originName: entry.originName,
      competitor: entry.competitor,
      description: classification.description || "No Guidance",
      validation,
      quote: entry.quote,
    });
  });

  return {
    missingProductQuotes,
    missingMilesOrigins,
    noGuidanceOrigins,
  };
};

export const buildRailMissingFreightSections = async ({
  railMissingQuotesForDisplay,
  destinationId,
  destinationIsCity,
  selectedProduct,
  selectedMonth,
  getMultiLocationDetails,
  validateMultiRoute,
}: {
  railMissingQuotesForDisplay: Quote[];
  destinationId: string;
  destinationIsCity?: boolean;
  selectedProduct: string;
  selectedMonth: string;
  getMultiLocationDetails: (originIds: string[]) => Promise<Map<string, any>>;
  validateMultiRoute: (
    params: MultiRouteValidationParams,
  ) => Promise<IRouteValidationResult[]>;
}): Promise<MissingRailFreightSectionItem[]> => {
  type PendingRailValidation = {
    originKey: string;
    originId: string;
    originName: string;
    competitor: boolean;
    quote: Quote;
    odPair: MultiRouteValidationOdPair;
  };

  const uniqueOriginIds = Array.from(
    new Set(
      railMissingQuotesForDisplay
        .map((quote) => quote.origin?.id || "")
        .filter(Boolean),
    ),
  );

  let locationDetailsMap: Map<string, any> = new Map();
  if (uniqueOriginIds.length > 0) {
    try {
      locationDetailsMap = await getMultiLocationDetails(uniqueOriginIds);
    } catch (error) {
      // Map stays empty — all entries will be excluded below
    }
  }

  const pendingValidations: PendingRailValidation[] = [];

  railMissingQuotesForDisplay.forEach((quote) => {
    const originId = quote.origin?.id || "";
    if (!originId) {
      return;
    }

    const locationDetails = locationDetailsMap.get(originId);
    if (!locationDetails) {
      return;
    }

    const outboundModes = Array.isArray(locationDetails?.outboundModes)
      ? locationDetails.outboundModes
      : [];
    if (
      !outboundModes.some(
        (mode: unknown) => String(mode).toUpperCase() === "RAIL",
      )
    ) {
      return;
    }

    const occupants = Array.isArray(locationDetails?.occupants)
      ? locationDetails.occupants
      : [];

    const matchedOccupant = occupants.find((occupant: any) =>
      occupantMatchesFavoriteType(occupant, quote),
    );

    if (!matchedOccupant?.id) {
      return;
    }

    pendingValidations.push({
      originKey: `${quote.competitor ? "competitor" : "koch"}-${originId}`,
      originId,
      originName: quote.origin?.name || "Unknown Origin",
      competitor: !!quote.competitor,
      quote,
      odPair: {
        originLocationId: originId,
        originOccupantId: matchedOccupant.id,
        destinationId,
        mode: "RAIL",
      },
    });
  });

  if (pendingValidations.length === 0) {
    return [];
  }

  let validationByOriginId = new Map<string, IRouteValidationResult>();

  try {
    const odPairs = pendingValidations.map((entry) => entry.odPair);
    const results = await validateMultiRoute({
      odPairsToValidate: odPairs,
      product: selectedProduct || "AMMONIA",
      shipDate: selectedMonth || "",
    });
    validationByOriginId = mapValidationResultsByOriginId(
      Array.isArray(results) ? results : [],
    );
  } catch (error) {
    return [];
  }

  return pendingValidations
    .map((entry) => {
      const railValidation = validationByOriginId.get(entry.originId);
      if (!railValidation) {
        return null;
      }

      return {
        originKey: entry.originKey,
        originId: entry.originId,
        originName: entry.originName,
        competitor: entry.competitor,
        railValidation,
        quote: entry.quote,
        isCity: destinationIsCity,
        originDetails: locationDetailsMap.get(entry.originId),
      };
    })
    .filter(
      (entry): entry is NonNullable<typeof entry> => !!entry,
    ) as MissingRailFreightSectionItem[];
};

export const loadRailMissingFreightSections = async ({
  missingFreightTab,
  shouldShowRailMissing,
  destinationId,
  destinationIsCity,
  railMissingQuotesForDisplay,
  selectedProduct,
  selectedMonth,
  getMultiLocationDetails,
  validateMultiRoute,
  setOriginsMissingRailFreight,
  setLoadingRailMissingFreight,
}: {
  missingFreightTab: string;
  shouldShowRailMissing: boolean;
  destinationId: string;
  destinationIsCity?: boolean;
  railMissingQuotesForDisplay: Quote[];
  selectedProduct: string;
  selectedMonth: string;
  getMultiLocationDetails: (originIds: string[]) => Promise<Map<string, any>>;
  validateMultiRoute: (
    params: MultiRouteValidationParams,
  ) => Promise<IRouteValidationResult[]>;
  setOriginsMissingRailFreight: (
    value: MissingRailFreightSectionItem[],
  ) => void;
  setLoadingRailMissingFreight: (value: boolean) => void;
}): Promise<void> => {
  if (
    missingFreightTab !== "RAIL" ||
    !shouldShowRailMissing ||
    !destinationId ||
    railMissingQuotesForDisplay.length === 0
  ) {
    setOriginsMissingRailFreight([]);
    return;
  }

  setLoadingRailMissingFreight(true);

  try {
    const results = await buildRailMissingFreightSections({
      railMissingQuotesForDisplay,
      destinationId,
      destinationIsCity,
      selectedProduct,
      selectedMonth,
      getMultiLocationDetails,
      validateMultiRoute,
    });

    setOriginsMissingRailFreight(results);
  } catch (error) {
    setOriginsMissingRailFreight([]);
  } finally {
    setLoadingRailMissingFreight(false);
  }
};

export const getLocationDetailsWithRetry = async ({
  locationId,
  fetchLocationDetails,
  maxAttempts = 3,
}: {
  locationId: string;
  fetchLocationDetails: (locationId: string) => Promise<any>;
  maxAttempts?: number;
}): Promise<any> => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fetchLocationDetails(locationId);
    } catch (error) {
      lastError = error;
      if (attempt >= maxAttempts) {
        break;
      }

      // eslint-disable-next-line no-await-in-loop
      await wait(200 * attempt);
    }
  }

  throw lastError;
};

const getPrimaryCarrier = (carriers: unknown): string => {
  if (!Array.isArray(carriers) || carriers.length === 0) {
    return "";
  }

  const primaryCarrier = carriers[0] as any;
  if (typeof primaryCarrier === "string") {
    return primaryCarrier;
  }

  return (
    primaryCarrier?.name || primaryCarrier?.carrier || primaryCarrier?.id || ""
  );
};

export const postCreateRailRequests = async ({
  originsToRequest,
  requestedBy,
  destinationStationState,
  destinationCarriers,
  selectedProduct,
  fallbackProduct,
  getLocationDetailsWithRetryFn,
}: {
  originsToRequest: Array<{ originId: string; originName: string }>;
  requestedBy: string;
  destinationStationState: string;
  destinationCarriers: unknown;
  selectedProduct?: string;
  fallbackProduct?: string;
  getLocationDetailsWithRetryFn: (locationId: string) => Promise<any>;
}): Promise<void> => {
  if (!originsToRequest.length) {
    return;
  }

  const destinationCarrier = getPrimaryCarrier(destinationCarriers);
  const requestProduct = (
    selectedProduct ||
    fallbackProduct ||
    "AMMONIA"
  ).toUpperCase();

  await Promise.all(
    originsToRequest.map(async (originEntry, index) => {
      const originDetails = await getLocationDetailsWithRetryFn(
        originEntry.originId,
      ).catch(() => undefined);

      return {
        requestId: `${originEntry.originId}-${Date.now()}-${index}`,
        requestedBy,
        originStationState: originDetails?.stationState || "",
        originCarrier: getPrimaryCarrier(originDetails?.carriers),
        destinationStationState,
        destinationCarrier,
        product: requestProduct,
      };
    }),
  );

  // await NitroService.createRailRateRequest(requests);
};

type SetRailLoadingByOriginKey = (
  updater: (prev: Record<string, boolean>) => Record<string, boolean>,
) => void;

const setRailRateLoading = ({
  setLoadingRailRateRequestByOriginKey,
  originKeys,
  isLoading,
}: {
  setLoadingRailRateRequestByOriginKey: SetRailLoadingByOriginKey;
  originKeys: string[];
  isLoading: boolean;
}) => {
  if (!originKeys.length) {
    return;
  }

  setLoadingRailRateRequestByOriginKey((prev) => {
    const next = { ...prev };
    originKeys.forEach((originKey) => {
      next[originKey] = isLoading;
    });
    return next;
  });
};

export const submitRailRateRequest = async ({
  payload,
  originsMissingRailFreight,
  pendingRailRateRequestOriginKeys,
  setLoadingRailRateRequestByOriginKey,
  postCreateRailRequestsFn,
  postRequestForRailRate,
  markRailRateRequested,
  setPendingRailRateRequestOriginKeys,
  savedNotification,
  failedNotification,
}: {
  payload: ISubmitDataRequestFormParams;
  originsMissingRailFreight: MissingRailFreightSectionItem[];
  pendingRailRateRequestOriginKeys: string[];
  setLoadingRailRateRequestByOriginKey: SetRailLoadingByOriginKey;
  postCreateRailRequestsFn: (
    originsToRequest: Array<{ originId: string; originName: string }>,
  ) => Promise<void>;
  postRequestForRailRate: (params: {
    payload: ISubmitDataRequestFormParams;
    selectedOriginEntries: MissingRailFreightSectionItem[];
  }) => Promise<void>;
  markRailRateRequested: (originKeys: string[]) => void;
  setPendingRailRateRequestOriginKeys: (originKeys: string[]) => void;
  savedNotification: (message: string) => void;
  failedNotification: (title: string, message: string) => void;
}): Promise<void> => {
  const selectedOriginKeysForRequest = originsMissingRailFreight
    .filter((originEntry) =>
      pendingRailRateRequestOriginKeys.includes(originEntry.originKey),
    )
    .filter((originEntry) => {
      if (!payload.selectedOrigins || payload.selectedOrigins.length === 0) {
        return true;
      }
      return payload.selectedOrigins.includes(originEntry.originName);
    })
    .map((originEntry) => originEntry.originKey);

  const selectedOriginEntries = originsMissingRailFreight
    .filter((originEntry) =>
      pendingRailRateRequestOriginKeys.includes(originEntry.originKey),
    )
    .filter((originEntry) => {
      if (!payload.selectedOrigins || payload.selectedOrigins.length === 0) {
        return true;
      }
      return payload.selectedOrigins.includes(originEntry.originName);
    });

  const selectedOriginsFilter =
    payload.selectedOrigins && payload.selectedOrigins.length > 0
      ? new Set(payload.selectedOrigins)
      : null;

  const originsForRequest = originsMissingRailFreight
    .filter((originEntry) =>
      pendingRailRateRequestOriginKeys.includes(originEntry.originKey),
    )
    .filter((originEntry) => {
      if (!selectedOriginsFilter) {
        return true;
      }
      return selectedOriginsFilter.has(originEntry.originName);
    })
    .map((originEntry) => ({
      originId: originEntry.originId,
      originName: originEntry.originName,
    }));

  setRailRateLoading({
    setLoadingRailRateRequestByOriginKey,
    originKeys: selectedOriginKeysForRequest,
    isLoading: true,
  });

  try {
    await postCreateRailRequestsFn(originsForRequest);
  } catch (error: any) {
    console.error("Failed to create rail request records", error);
    failedNotification(
      "Failed to submit rail rate request",
      error?.message || "Unknown error",
    );
    setRailRateLoading({
      setLoadingRailRateRequestByOriginKey,
      originKeys: selectedOriginKeysForRequest,
      isLoading: false,
    });
    return;
  }

  try {
    await postRequestForRailRate({
      payload,
      selectedOriginEntries,
    });
  } catch (error: any) {
    console.error("Failed to submit rail freight rate request", error);
    failedNotification(
      "Failed to submit rail rate request",
      error?.message || "Unknown error",
    );
    setRailRateLoading({
      setLoadingRailRateRequestByOriginKey,
      originKeys: selectedOriginKeysForRequest,
      isLoading: false,
    });
    return;
  }

  markRailRateRequested(selectedOriginKeysForRequest);
  setRailRateLoading({
    setLoadingRailRateRequestByOriginKey,
    originKeys: selectedOriginKeysForRequest,
    isLoading: false,
  });
  setPendingRailRateRequestOriginKeys([]);
  savedNotification("Rail rate request submitted");
};

export const loadMissingFreightSectionsWithState = async ({
  missingFreightLoadInFlight,
  lastMissingFreightLoadAt,
  pendingMissingFreightThrottleTimer,
  minReloadIntervalMs = 2500,
  destinationId,
  truckMissingQuotesForDisplay,
  selectedProduct,
  fallbackProduct,
  selectedMonth,
  getMultiLocationDetailsMap,
  validateMultiRoute,
  setMissingProductQuotes,
  setMissingMilesOrigins,
  setOriginsWithNoGuidance,
  setLoadingMissingMiles,
  onThrottledReload,
}: {
  missingFreightLoadInFlight: { current: boolean };
  lastMissingFreightLoadAt: { current: number };
  pendingMissingFreightThrottleTimer: { current: NodeJS.Timeout | null };
  minReloadIntervalMs?: number;
  destinationId: string;
  truckMissingQuotesForDisplay: Quote[];
  selectedProduct?: string;
  fallbackProduct?: string;
  selectedMonth?: string;
  getMultiLocationDetailsMap: (
    originIds: string[],
  ) => Promise<Map<string, any>>;
  validateMultiRoute: (
    params: MultiRouteValidationParams,
  ) => Promise<IRouteValidationResult[]>;
  setMissingProductQuotes: (quotes: Quote[]) => void;
  setMissingMilesOrigins: (
    origins: Array<{
      originId: string;
      originName: string;
      competitor: boolean;
      validation: any;
    }>,
  ) => void;
  setOriginsWithNoGuidance: (origins: NoGuidanceSectionItem[]) => void;
  setLoadingMissingMiles: (loading: boolean) => void;
  onThrottledReload: () => void;
}): Promise<void> => {
  const missingFreightLoadInFlightRef = missingFreightLoadInFlight;
  const lastMissingFreightLoadAtRef = lastMissingFreightLoadAt;
  const pendingMissingFreightThrottleTimerRef =
    pendingMissingFreightThrottleTimer;

  if (missingFreightLoadInFlightRef.current) {
    return;
  }

  const now = Date.now();
  const elapsedMs = now - lastMissingFreightLoadAtRef.current;
  if (elapsedMs < minReloadIntervalMs) {
    if (!pendingMissingFreightThrottleTimerRef.current) {
      const remainingMs = minReloadIntervalMs - elapsedMs;
      pendingMissingFreightThrottleTimerRef.current = setTimeout(() => {
        pendingMissingFreightThrottleTimerRef.current = null;
        onThrottledReload();
      }, remainingMs);
    }
    return;
  }

  if (!destinationId || truckMissingQuotesForDisplay.length === 0) {
    setMissingProductQuotes([]);
    setMissingMilesOrigins([]);
    setOriginsWithNoGuidance([]);
    return;
  }

  lastMissingFreightLoadAtRef.current = Date.now();
  missingFreightLoadInFlightRef.current = true;
  setLoadingMissingMiles(true);

  try {
    const sections = await buildMissingFreightSections({
      truckMissingQuotesForDisplay,
      destinationId,
      selectedProduct: selectedProduct || fallbackProduct || "AMMONIA",
      selectedMonth: selectedMonth || "",
      getMultiLocationDetails: (originIds: string[]) =>
        getMultiLocationDetailsMap(originIds),
      validateMultiRoute,
    });

    setMissingProductQuotes(sections.missingProductQuotes);
    setMissingMilesOrigins(
      sections.missingMilesOrigins.map((itemSection) => ({
        originId: itemSection.originId,
        originName: itemSection.originName,
        competitor: itemSection.competitor,
        validation: itemSection.validation,
      })),
    );
    setOriginsWithNoGuidance(sections.noGuidanceOrigins);
  } catch (error) {
    console.warn("Unable to classify missing freight sections", error);
    setMissingProductQuotes([]);
    setMissingMilesOrigins([]);
    setOriginsWithNoGuidance([]);
  } finally {
    setLoadingMissingMiles(false);
    missingFreightLoadInFlightRef.current = false;
  }
};

export const handleLookupRailStationNextWithState = async ({
  station,
  destinationId,
  getLocationDetailsWithRetryFn,
  getCarriersByRailStation,
  setSelectedLookupStation,
  setSelectedLookupScrsId,
  setSelectedLookupStationState,
  setLookupUserAddedCarriers,
  setShowRailStationLookupPanel,
  setShowRailCarrierReviewPanel,
  setLoadingLookupRailCarriers,
  setLookupRailCarriers,
}: {
  station: {
    name: string;
    value: string;
    scrsId?: string | null;
    carriers?: string[];
    stationState?: string;
  };
  destinationId: string;
  getLocationDetailsWithRetryFn: (locationId: string) => Promise<any>;
  getCarriersByRailStation: (params: {
    stationState: string;
  }) => Promise<any[]>;
  setSelectedLookupStation: (value: string) => void;
  setSelectedLookupScrsId: (value: string) => void;
  setSelectedLookupStationState: (value: string) => void;
  setLookupUserAddedCarriers: (value: string[]) => void;
  setShowRailStationLookupPanel: (value: boolean) => void;
  setShowRailCarrierReviewPanel: (value: boolean) => void;
  setLoadingLookupRailCarriers: (value: boolean) => void;
  setLookupRailCarriers: (value: Record<string, boolean>) => void;
}): Promise<void> => {
  setSelectedLookupStation(station.value);
  setSelectedLookupScrsId(station.scrsId || "");
  setSelectedLookupStationState(station.stationState || "");
  setLookupUserAddedCarriers([]);
  setShowRailStationLookupPanel(true);
  setShowRailCarrierReviewPanel(true);
  setLoadingLookupRailCarriers(true);

  try {
    const scrsCarriers = (station.carriers || []).filter(Boolean);

    if (scrsCarriers.length > 0) {
      const carriersMap = scrsCarriers.reduce<Record<string, boolean>>(
        (acc, carrierId) => {
          acc[carrierId] = true;
          return acc;
        },
        {},
      );

      setLookupRailCarriers(carriersMap);
    } else {
      const [freshLocationDetails, carrierRecords] = await Promise.all([
        getLocationDetailsWithRetryFn(destinationId),
        getCarriersByRailStation({
          stationState: station.value,
        }),
      ]);

      const carriersMap = buildCarriersMap(
        carrierRecords,
        freshLocationDetails,
        station.value,
      );

      setLookupRailCarriers(carriersMap);
    }
  } catch (error) {
    setLookupRailCarriers({});
    console.error("Error loading carriers:", error);
  } finally {
    setLoadingLookupRailCarriers(false);
  }
};

export const applyAddMissingProductWithState = async ({
  quote,
  originId,
  addProductKey,
  missingProductName,
  selectedOccupants,
  existingLocationDetails,
  addMissingProductInFlight,
  addMissingProductCooldownUntil,
  getLocationDetailsWithRetryFn,
  updateOccupants,
  selectedProduct,
  fallbackProduct,
  destinationId,
  destinationIsCity,
  selectedMonth,
  validateRoute,
  setLoadingAddProductByKey,
  setMissingProductQuotes,
  setMissingMilesOrigins,
  setOriginsWithNoGuidance,
  setLocallyValidatedOriginKeys,
  refreshValidatedOriginQuote,
  onSuccess,
  savedNotification,
  failedNotification,
}: {
  quote: Quote;
  originId: string;
  addProductKey: string;
  missingProductName: string;
  selectedOccupants: any[];
  existingLocationDetails?: any;
  addMissingProductInFlight: { current: boolean };
  addMissingProductCooldownUntil: { current: number };
  getLocationDetailsWithRetryFn: (locationId: string) => Promise<any>;
  updateOccupants: (params: any) => Promise<any>;
  selectedProduct?: string;
  fallbackProduct?: string;
  destinationId: string;
  destinationIsCity: boolean;
  selectedMonth?: string;
  validateRoute: (params: {
    mode: string;
    product: string;
    originLocationId: string;
    originOccupantId: string;
    destinationId: string;
    shipDate: string;
    isCity: boolean;
  }) => Promise<any>;
  setLoadingAddProductByKey: (
    updater: (prev: Record<string, boolean>) => Record<string, boolean>,
  ) => void;
  setMissingProductQuotes: (updater: (prev: Quote[]) => Quote[]) => void;
  setMissingMilesOrigins: (updater: (prev: any[]) => any[]) => void;
  setOriginsWithNoGuidance: (
    updater: (prev: NoGuidanceSectionItem[]) => NoGuidanceSectionItem[],
  ) => void;
  setLocallyValidatedOriginKeys: (
    updater: (prev: Set<string>) => Set<string>,
  ) => void;
  refreshValidatedOriginQuote: (
    quote: Quote,
    originId: string,
  ) => Promise<void>;
  onSuccess?: () => void | Promise<void>;
  savedNotification: (message: string) => void;
  failedNotification: (title: string, message: string) => void;
}): Promise<void> => {
  const addMissingProductInFlightRef = addMissingProductInFlight;
  const addMissingProductCooldownUntilRef = addMissingProductCooldownUntil;

  if (
    addMissingProductInFlightRef.current ||
    Date.now() < addMissingProductCooldownUntilRef.current
  ) {
    return;
  }

  addMissingProductInFlightRef.current = true;
  const normalizedMissingProductName = (missingProductName || "").toUpperCase();

  setLoadingAddProductByKey((prev) => ({
    ...prev,
    [addProductKey]: true,
  }));

  try {
    if (!selectedOccupants.length) {
      failedNotification(
        "Failed to add missing product.",
        "Select at least one occupant.",
      );
      return;
    }

    const currentLocationDetails =
      existingLocationDetails ||
      (await getLocationDetailsWithRetryFn(originId));
    const currentOccupants = Array.isArray(currentLocationDetails?.occupants)
      ? currentLocationDetails?.occupants || []
      : [];

    if (!currentOccupants.length) {
      failedNotification(
        "Failed to add missing product.",
        "No occupants found for this origin.",
      );
      return;
    }

    const selectedIdentifiers = new Set(
      selectedOccupants
        .filter((occupant: any) => occupantMatchesFavoriteType(occupant, quote))
        .map((occupant: any) => occupant?.id || occupant?.name),
    );

    if (!selectedIdentifiers.size) {
      return;
    }

    const occupantsToUpdate = currentOccupants.map((occupant: any) => {
      const identifier = occupant?.id || occupant?.name;
      if (!selectedIdentifiers.has(identifier)) {
        return occupant;
      }

      const occupantProducts = Array.isArray(occupant?.products)
        ? occupant.products
        : [];
      const hasProduct = occupantProducts.some(
        (productEntry: any) =>
          typeof productEntry?.product === "string" &&
          productEntry.product.toUpperCase() === normalizedMissingProductName,
      );

      if (hasProduct) {
        return occupant;
      }

      return {
        ...occupant,
        products: [
          ...occupantProducts,
          {
            product: normalizedMissingProductName,
            quantity: 0,
          },
        ],
      };
    });

    await updateOccupants({
      id: originId,
      occupants: occupantsToUpdate,
    });

    const updatedLocationDetails = await getLocationDetailsWithRetryFn(
      originId,
    );
    const updatedOccupants = Array.isArray(updatedLocationDetails?.occupants)
      ? updatedLocationDetails.occupants
      : [];

    const matchesQuoteKey = (entry: Quote) =>
      entry.origin.id === quote.origin.id &&
      !!entry.competitor === !!quote.competitor &&
      (entry.mode || "").toUpperCase() === (quote.mode || "").toUpperCase();

    setMissingProductQuotes((prev) =>
      prev.filter((entry) => !matchesQuoteKey(entry)),
    );

    const matchedOccupantForValidation = updatedOccupants.find(
      (occupant: any) => {
        if (!occupantMatchesFavoriteType(occupant, quote)) {
          return false;
        }

        const identifier = occupant?.id || occupant?.name;
        if (!selectedIdentifiers.has(identifier)) {
          return false;
        }

        const occupantProducts = Array.isArray(occupant?.products)
          ? occupant.products
          : [];
        return occupantProducts.some(
          (productEntry: any) =>
            typeof productEntry?.product === "string" &&
            productEntry.product.toUpperCase() === normalizedMissingProductName,
        );
      },
    );

    if (matchedOccupantForValidation?.id) {
      try {
        const validation = await validateRoute({
          mode: "TRUCK",
          product: selectedProduct || fallbackProduct || "AMMONIA",
          originLocationId: originId,
          originOccupantId: matchedOccupantForValidation.id,
          destinationId,
          shipDate: selectedMonth || "",
          isCity: !!destinationIsCity,
        });

        const noGuidanceOriginKey = `${
          quote.competitor ? "competitor" : "koch"
        }-${originId}`;
        const nextNoGuidanceItem: NoGuidanceSectionItem = {
          originKey: noGuidanceOriginKey,
          originId,
          originName: quote.origin.name || "Unknown Origin",
          competitor: !!quote.competitor,
          description: "No Guidance",
          validation,
          quote,
        };

        const classification = classifyTruckValidation(validation as any);

        if (classification.section === "no-guidance") {
          setMissingMilesOrigins((prev) =>
            prev.filter(
              (entry) =>
                !(
                  entry.originId === originId &&
                  entry.competitor === !!quote.competitor
                ),
            ),
          );
          setOriginsWithNoGuidance((prev) => {
            const filtered = prev.filter(
              (entry) => entry.originKey !== noGuidanceOriginKey,
            );
            return [
              ...filtered,
              {
                ...nextNoGuidanceItem,
                description: classification.description || "No Guidance",
              },
            ];
          });
        } else if (classification.section === "missing-miles") {
          setOriginsWithNoGuidance((prev) =>
            prev.filter((entry) => entry.originKey !== noGuidanceOriginKey),
          );
          setMissingMilesOrigins((prev) => {
            const filtered = prev.filter(
              (entry) =>
                !(
                  entry.originId === originId &&
                  entry.competitor === !!quote.competitor
                ),
            );
            return [
              ...filtered,
              {
                originId,
                originName: quote.origin.name || "Unknown Origin",
                competitor: !!quote.competitor,
                validation,
              },
            ];
          });
        } else {
          setMissingMilesOrigins((prev) =>
            prev.filter(
              (entry) =>
                !(
                  entry.originId === originId &&
                  entry.competitor === !!quote.competitor
                ),
            ),
          );
          setOriginsWithNoGuidance((prev) =>
            prev.filter((entry) => entry.originKey !== noGuidanceOriginKey),
          );
          setLocallyValidatedOriginKeys(
            (prev) => new Set([...prev, getQuoteIdentityKey(quote)]),
          );

          try {
            await refreshValidatedOriginQuote(quote, originId);
          } catch (refreshError) {
            console.warn(
              "Unable to refresh quote freight for validated origin",
              refreshError,
            );
          }
        }
      } catch (validationError) {
        const fallbackNoGuidanceKey = `${
          quote.competitor ? "competitor" : "koch"
        }-${originId}`;
        setMissingMilesOrigins((prev) =>
          prev.filter(
            (entry) =>
              !(
                entry.originId === originId &&
                entry.competitor === !!quote.competitor
              ),
          ),
        );
        setOriginsWithNoGuidance((prev) => {
          const filtered = prev.filter(
            (entry) => entry.originKey !== fallbackNoGuidanceKey,
          );
          return [
            ...filtered,
            {
              originKey: fallbackNoGuidanceKey,
              originId,
              originName: quote.origin.name || "Unknown Origin",
              competitor: !!quote.competitor,
              description: "No Guidance",
              validation: {},
              quote,
            },
          ];
        });
        console.warn(
          "Unable to re-validate origin after adding product",
          validationError,
        );
      }
    }

    savedNotification(`Successfully added ${missingProductName}.`);
    if (onSuccess) {
      try {
        await onSuccess();
      } catch (onSuccessError) {
        console.warn("Post add-missing-product refresh failed", onSuccessError);
      }
    }
  } catch (err: any) {
    failedNotification(
      "Failed to add missing product.",
      err?.message || "Unknown error",
    );
  } finally {
    addMissingProductInFlightRef.current = false;
    addMissingProductCooldownUntilRef.current = Date.now() + 1500;
    setLoadingAddProductByKey((prev) => ({
      ...prev,
      [addProductKey]: false,
    }));
  }
};

export const submitAddMissingProductForSelectedOccupantsWithState = async ({
  addMissingProductModalState,
  selectedOccupantKeys,
  addMissingProductInFlight,
  addMissingProductCooldownUntil,
  setSubmittingAddMissingProduct,
  setAddMissingProductModalState,
  setSelectedOccupantKeys,
  applyAddMissingProductFn,
}: {
  addMissingProductModalState: AddMissingProductModalState | null;
  selectedOccupantKeys: string[];
  addMissingProductInFlight: { current: boolean };
  addMissingProductCooldownUntil: { current: number };
  setSubmittingAddMissingProduct: (value: boolean) => void;
  setAddMissingProductModalState: (
    value: AddMissingProductModalState | null,
  ) => void;
  setSelectedOccupantKeys: (value: string[]) => void;
  applyAddMissingProductFn: (params: {
    quote: Quote;
    originId: string;
    addProductKey: string;
    missingProductName: string;
    selectedOccupants: any[];
  }) => Promise<void>;
}): Promise<void> => {
  if (!addMissingProductModalState || !selectedOccupantKeys.length) return;
  if (
    addMissingProductInFlight.current ||
    Date.now() < addMissingProductCooldownUntil.current
  ) {
    return;
  }

  setSubmittingAddMissingProduct(true);
  try {
    const selectedOccupants = addMissingProductModalState.occupants
      .filter((occupantItem) => selectedOccupantKeys.includes(occupantItem.key))
      .map((occupantItem) => occupantItem.occupant);

    await applyAddMissingProductFn({
      quote: addMissingProductModalState.quote,
      originId: addMissingProductModalState.originId,
      addProductKey: addMissingProductModalState.addProductKey,
      missingProductName: addMissingProductModalState.missingProductName,
      selectedOccupants,
    });
    setAddMissingProductModalState(null);
    setSelectedOccupantKeys([]);
  } finally {
    setSubmittingAddMissingProduct(false);
  }
};

export const handleAddMissingProductWithState = async ({
  quote,
  addMissingProductInFlight,
  addMissingProductCooldownUntil,
  selectedProduct,
  fallbackProduct,
  getLocationDetailsWithRetryFn,
  setAddMissingProductModalState,
  setSelectedOccupantKeys,
  failedNotification,
  applyAddMissingProductFn,
}: {
  quote: Quote;
  addMissingProductInFlight: { current: boolean };
  addMissingProductCooldownUntil: { current: number };
  selectedProduct?: string;
  fallbackProduct?: string;
  getLocationDetailsWithRetryFn: (locationId: string) => Promise<any>;
  setAddMissingProductModalState: (
    value: AddMissingProductModalState | null,
  ) => void;
  setSelectedOccupantKeys: (value: string[]) => void;
  failedNotification: (title: string, message: string) => void;
  applyAddMissingProductFn: (params: {
    quote: Quote;
    originId: string;
    addProductKey: string;
    missingProductName: string;
    selectedOccupants: any[];
    existingLocationDetails?: any;
  }) => Promise<void>;
}): Promise<void> => {
  if (
    addMissingProductInFlight.current ||
    Date.now() < addMissingProductCooldownUntil.current
  ) {
    return;
  }

  const originId = quote.origin.id;
  if (!originId) return;

  const missingProductName = formatProductName(
    selectedProduct || fallbackProduct || "AMMONIA",
  );
  const favoriteOriginType = getFavoriteOriginType(quote);
  const addProductKey = `${originId}:${favoriteOriginType}:${missingProductName.toUpperCase()}`;

  try {
    const currentLocationDetails = await getLocationDetailsWithRetryFn(
      originId,
    );

    if (!currentLocationDetails?.occupants?.length) {
      failedNotification(
        "Failed to add missing product.",
        "No occupants found for this origin.",
      );
      return;
    }

    const currentOccupants = Array.isArray(currentLocationDetails.occupants)
      ? currentLocationDetails.occupants
      : [];

    const matchingTypeOccupants = currentOccupants.filter((occupant: any) =>
      occupantMatchesFavoriteType(occupant, quote),
    );

    const occupantSelectionItems: OccupantSelectionItem[] =
      currentOccupants.map((occupant: any, index: number) => ({
        key: getOccupantSelectionKey(occupant, index),
        occupant,
        label: occupant?.name || `Occupant ${index + 1}`,
        disabled: !occupantMatchesFavoriteType(occupant, quote),
      }));

    if (currentOccupants.length > 1) {
      setAddMissingProductModalState({
        quote,
        originId,
        addProductKey,
        missingProductName,
        occupants: occupantSelectionItems,
      });
      setSelectedOccupantKeys([]);
      return;
    }

    if (!matchingTypeOccupants.length) {
      return;
    }

    const targetOccupant = matchingTypeOccupants[0];
    if (!targetOccupant) {
      failedNotification(
        "Failed to add missing product.",
        "No valid occupant found for this origin.",
      );
      return;
    }

    await applyAddMissingProductFn({
      quote,
      originId,
      addProductKey,
      missingProductName,
      selectedOccupants: [targetOccupant],
      existingLocationDetails: currentLocationDetails,
    });
  } catch (err: any) {
    failedNotification(
      "Failed to add missing product.",
      err?.message || "Unknown error",
    );
  }
};

export const requestSingleRailRateForOriginWithState = async ({
  railOrigin,
  setLoadingRailRateRequestByOriginKey,
  postCreateRailRequestsFn,
  postRequestForRailRate,
  markRailRateRequested,
  setPendingRailRateRequestOriginKeys,
  setSelectedRailRateRequestOrigins,
  setSelectedRailRateRequestOriginName,
  setShowRailRateRequestPanel,
  savedNotification,
  failedNotification,
}: {
  railOrigin: MissingRailFreightSectionItem;
  setLoadingRailRateRequestByOriginKey: (
    updater: (prev: Record<string, boolean>) => Record<string, boolean>,
  ) => void;
  postCreateRailRequestsFn: (
    originsToRequest: Array<{ originId: string; originName: string }>,
  ) => Promise<void>;
  postRequestForRailRate: (
    selectedOrigins: MissingRailFreightSectionItem[],
  ) => Promise<void>;
  markRailRateRequested: (originKeys: string[]) => void;
  setPendingRailRateRequestOriginKeys: (value: string[]) => void;
  setSelectedRailRateRequestOrigins: (value: string[]) => void;
  setSelectedRailRateRequestOriginName: (value: string) => void;
  setShowRailRateRequestPanel: (value: boolean) => void;
  savedNotification: (message: string) => void;
  failedNotification: (title: string, message: string) => void;
}): Promise<void> => {
  // Don't display RailRateRequestPanel if the origin is a competitor, just submit the request immediately
  if (railOrigin.competitor) {
    try {
      setLoadingRailRateRequestByOriginKey((prev) => ({
        ...prev,
        [railOrigin.originKey]: true,
      }));
      await postCreateRailRequestsFn([
        {
          originId: railOrigin.originId,
          originName: railOrigin.originName,
        },
      ]);
      await postRequestForRailRate([railOrigin]);
      markRailRateRequested([railOrigin.originKey]);
      savedNotification("Rail rate request submitted");
    } catch (error: any) {
      failedNotification(
        "Failed to submit rail rate request",
        error?.message || "Unknown error",
      );
    } finally {
      setLoadingRailRateRequestByOriginKey((prev) => ({
        ...prev,
        [railOrigin.originKey]: false,
      }));
    }
    return;
  }

  setPendingRailRateRequestOriginKeys([railOrigin.originKey]);
  setSelectedRailRateRequestOrigins([]);
  setSelectedRailRateRequestOriginName(railOrigin.originName);
  setShowRailRateRequestPanel(true);
};

export interface ISortableQuote {
  maxPrice?: number | null;
  freight?: {
    totalFreight?: number | null;
  } | null;
  [key: string]: any;
}

export const sortQuotesWithFreight = <T extends ISortableQuote>(
  quotes: T[],
): T[] => {
  return [...quotes].sort((a, b) => {
    const aHasMax = a.maxPrice !== null && a.maxPrice !== undefined;
    const bHasMax = b.maxPrice !== null && b.maxPrice !== undefined;

    if (aHasMax && !bHasMax) return -1;
    if (!aHasMax && bHasMax) return 1;

    if (aHasMax && bHasMax) {
      const costA = (a.freight?.totalFreight ?? 0) + (a.maxPrice ?? 0);
      const costB = (b.freight?.totalFreight ?? 0) + (b.maxPrice ?? 0);
      return costA - costB;
    }

    const freightA = a.freight?.totalFreight ?? 0;
    const freightB = b.freight?.totalFreight ?? 0;
    return freightA - freightB;
  });
};

/**
 * 1)
If ‘hasTruckRoute: False.’ - put in origins-with-no-guidance-section, description: No Route

2)
If ‘hasTruckMileage: False’ - put in missing-miles-section

3)
If ‘hasTruckMileage: true
hasTruckRoute: true
hasValidTruckRateForShipWindow: False
hasValidTruckRateForShipWindowAndMileage: False’,

- put in origins-with-no-guidance-section
, description: No Route

4)
If ‘hasTruckMileage: true
hasTruckRoute: True
hasValidTruckRateForShipWindow: false
hasValidTruckRateForShipWindowAndMileage: false’, - put in origins-with-no-guidance-section, description: Expired

5)

If ‘hasTruckMileage: True
hasTruckRoute: True
hasValidTruckRateForShipWindow: True
hasValidTruckRateForShipWindowAndMileage: false’, - put in origins-with-no-guidance-section, description:  `Outside Mileage Band - ${truckMileage} mile`;

 */
